import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { DynamicFilterConfig, SiteService, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { CashRequest, CashRequestStatus } from '../../../models/cash-request/cash-request';
import { CashRequestProxyService } from '../../../proxies/cash-request.proxy.service';
import { SiteCompanyService } from '../../../services/site-company.service';

interface CashRequestFilters {
  status?: number | string | null;
  weekFrom?: Date | null;
  weekTo?: Date | null;
}

/** Cash Request dashboard — summary + collapsible filters + the requests table. */
@Component({
  selector: 'app-cash-requests',
  templateUrl: './cash-requests.component.html',
  styleUrl: './cash-requests.component.scss',
})
export class CashRequestsComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly service = inject(CashRequestProxyService);
  private readonly siteCompany = inject(SiteCompanyService);
  private readonly translate = inject(TranslateService);

  private companyRef: string | null = null;

  readonly isLoading = signal(0);
  readonly requests = signal<CashRequest[]>([]);
  readonly filtered = signal<CashRequest[]>([]);
  readonly filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'cash-request-filters', config: this.filterConfig });
  readonly showFilters = signal(true);

  readonly totalRecords = computed(() => this.filtered().length);
  readonly currentPage = signal(1);
  readonly pageSize = signal(25);

  readonly paginatedRequests = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.filtered().slice(start, start + this.pageSize());
  });

  // ── Summary (over all requests, independent of filters) ──────────────────────
  readonly draftCount = computed(() => this.requests().filter((r) => r.status === CashRequestStatus.Draft).length);
  readonly pendingCount = computed(() => this.requests().filter((r) => r.status === CashRequestStatus.Submitted).length);
  readonly approvedCount = computed(() => this.requests().filter((r) => r.status === CashRequestStatus.Approved).length);
  readonly approvedTotal = computed(() =>
    this.requests()
      .filter((r) => r.status === CashRequestStatus.Approved)
      .reduce((sum, r) => sum + (r.totalAmount ?? 0), 0),
  );

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as CashRequestFilters));
  }

  ngOnInit(): void {
    this.filterConfig.set(this.buildFilterConfig());
    this.isLoading.update((v) => v + 1);
    this.siteCompany.getFinancialCompanyId(SiteService.getSelectedSiteId()).subscribe({
      next: (id) => {
        this.companyRef = id;
        this.isLoading.update((v) => v - 1);
        this.load();
      },
      error: () => this.isLoading.update((v) => v - 1),
    });
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private load(): void {
    if (!this.companyRef) return;
    this.startLoading();
    this.service
      .search(this.companyRef)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const data = response.data ?? [];
          this.requests.set(data);
          this.searchWithFilters(this.filterState.applied() as CashRequestFilters);
        },
        error: () => {
          this.requests.set([]);
          this.filtered.set([]);
        },
      });
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'select',
          formControlName: 'status',
          label: this.t('Status', 'Status'),
          options: [
            { label: this.t('Draft', 'Draft'), value: CashRequestStatus.Draft },
            { label: this.t('Submitted', 'Submitted'), value: CashRequestStatus.Submitted },
            { label: this.t('Approved', 'Approved'), value: CashRequestStatus.Approved },
            { label: this.t('Cancelled', 'Cancelled'), value: CashRequestStatus.Cancelled },
          ],
          showClear: true,
          defaultVisible: true,
        },
        { type: 'date', formControlName: 'weekFrom', label: this.t('CashRequest_WeekFrom', 'Week from'), defaultVisible: true },
        { type: 'date', formControlName: 'weekTo', label: this.t('CashRequest_WeekTo', 'Week to'), defaultVisible: true },
      ],
    };
  }

  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private searchWithFilters(filters: CashRequestFilters): void {
    const status = filters.status === null || filters.status === undefined || filters.status === '' ? null : Number(filters.status);
    const from = filters.weekFrom ? new Date(filters.weekFrom).getTime() : null;
    const to = filters.weekTo ? new Date(filters.weekTo).getTime() : null;

    this.filtered.set(
      this.requests().filter((r) => {
        if (status !== null && r.status !== status) return false;
        const start = new Date(r.weekStart).getTime();
        if (from !== null && start < from) return false;
        if (to !== null && start > to) return false;
        return true;
      }),
    );
    this.currentPage.set(1);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
  }

  statusLabel(status: number): string {
    return CashRequestStatus[status] ?? '';
  }

  statusSeverity(status: number): 'success' | 'warn' | 'info' | 'danger' | 'secondary' {
    switch (status) {
      case CashRequestStatus.Approved:
        return 'success';
      case CashRequestStatus.Submitted:
        return 'info';
      case CashRequestStatus.Cancelled:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  redirectToDetails(id?: string): void {
    this.router.navigate(['/cash-request-details'], { queryParams: id ? { id } : {} });
  }

  trackById = (_: number, r: CashRequest) => r.id;
}
